import { useRef, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, Loader2, Sparkles, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { thumb } from "@/components/LibraryPicker";

export interface Tile { image: string | null; image_ai: boolean; image_redo: boolean }

interface Props { slug: string; index: number | null; label: string; tile: Tile; onPick: () => void; onDone: () => void }

export function ImageTile({ slug, index, label, tile, onPick, onDone }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<"" | "upload" | "ai">("");
  const tid = `${slug}-${index ?? "hero"}`;
  const upload = async (f: File) => {
    setBusy("upload");
    const fd = new FormData(); fd.append("file", f);
    const url = index === null ? `/admin/posts/${slug}/image` : `/admin/posts/${slug}/project-image/${index}`;
    try { await api.post(url, fd); toast.success("Photo replaced"); onDone(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const remake = async () => {
    if (!window.confirm("Make a new AI example image for this spot? (about $0.05)")) return;
    setBusy("ai");
    try { await api.post("/admin/images/remake", { slug, index }, { timeout: 120000 }); toast.success("New example image made"); onDone(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const badge = !tile.image ? ["Missing", "bg-[#F08A6C]"] : tile.image_ai ? [tile.image_redo ? "Example · to remake" : "Example", "bg-[#F6D776]"] : ["My photo", "bg-[#9BC9A8]"];
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border-2 border-[#23324A]/15 bg-white" data-testid={`image-tile-${tid}`}>
      <div className="relative aspect-[4/3] bg-[#F4EFE6]">
        {tile.image && <img src={thumb(tile.image)} alt={label} loading="lazy" className="h-full w-full object-cover" />}
        <span className={`absolute left-2 top-2 rounded-full border border-[#23324A] px-2 py-0.5 text-[10px] font-bold text-[#23324A] ${badge[1]}`} data-testid={`image-badge-${tid}`}>{badge[0]}</span>
        {busy && <div className="absolute inset-0 grid place-items-center bg-white/70"><Loader2 className="h-6 w-6 animate-spin text-[#23324A]" /></div>}
      </div>
      <p className="truncate px-2 pt-2 text-xs font-semibold text-[#23324A]" title={label}>{label}</p>
      <div className="grid grid-cols-3 gap-1 p-2 [&>button]:inline-flex [&>button]:min-w-0 [&>button]:items-center [&>button]:justify-center [&>button]:gap-1 [&>button]:px-1">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} data-testid={`image-file-${tid}`} />
        <button className="chip" disabled={!!busy} onClick={() => fileRef.current?.click()} title="Upload a photo" data-testid={`image-upload-${tid}`}><Upload className="h-3 w-3" /><span className="hidden 2xl:inline">Upload</span></button>
        <button className="chip" disabled={!!busy} onClick={onPick} title="Pick from library" data-testid={`image-pick-${tid}`}><FolderOpen className="h-3 w-3" /><span className="hidden 2xl:inline">Library</span></button>
        <button className="chip" disabled={!!busy} onClick={remake} title="Remake with AI" data-testid={`image-remake-${tid}`}><Sparkles className="h-3 w-3" /></button>
      </div>
    </div>
  );
}
